import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { IBulkActionItem } from "@ryde/controls";
import {
  useModalConfirm,
  useRidesBulkActions,
  useSelectedIds,
} from "@ryde/redux";
import {
  bulkJourneyAssignFormDrawerAF,
  bulkSelectedControlsAtomFamily,
  bulkUpdateFormDrawerAF,
  ConfirmResult,
  Log,
  noop,
  reportBulkOperationsDrawerAF,
  ReportTemplateIdEnum,
  RideCrudModeKey,
  RydeUiUnit,
} from "@ryde/shared";

import { buildRideBulkAction } from "../../../../rides/drawers/parts";

const RIDE_ENTITY: RydeUiUnit = RideCrudModeKey.uiUnit;

export const useBulkOperationsRidesReportActions = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const { selectedRowsIds: selectedRideIds } = useSelectedIds();

  const [bulkSelectedFields, setBulkSelectedFields] = useRecoilState(
    bulkSelectedControlsAtomFamily(RIDE_ENTITY),
  );

  const setBulkUpdateFormDrawerOpened = useSetRecoilState(
    bulkUpdateFormDrawerAF(RIDE_ENTITY),
  );

  const setBulkJourneyAssignFormDrawerOpened = useSetRecoilState(
    bulkJourneyAssignFormDrawerAF(RIDE_ENTITY),
  );

  const setRidesReportBulkOperationsDrawer = useSetRecoilState(
    reportBulkOperationsDrawerAF(ReportTemplateIdEnum.RIDE_ANALYTICS_REPORT),
  );

  const {
    bulkRebuildRoutes,
    bulkSendToSupplier,
    bulkRecalculatePrice,
    bulkFitVehicleAndRecalculate,
  } = useRidesBulkActions();

  const closeDrawer = useEvent(() =>
    setRidesReportBulkOperationsDrawer?.(false),
  );

  const handleUpdateClick = useEvent(() => {
    closeDrawer();
    setBulkUpdateFormDrawerOpened?.(true);

    if (bulkSelectedFields?.length) {
      setBulkSelectedFields([]);
    }
  });

  const handleJourneyAssignClick = useEvent(() => {
    closeDrawer();
    setBulkJourneyAssignFormDrawerOpened?.(true);
  });

  const handleRecalculatePriceClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.RECALCULATE_PRICE_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const result = await bulkRecalculatePrice({ ids: selectedRideIds });

      if (!result?.success) {
        Log.warnAndToast(
          t(`general.RIDES_ACTIONS_WARNINGS.BULK_RECALCULATE_PRICE_CANCELED`),
          selectedRideIds,
        );
      }
    }
  });

  const handleFitVehicleAndRecalculateClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.FIT_VEHICLE_AND_RECALCULATE_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const result = await bulkFitVehicleAndRecalculate(selectedRideIds);

      if (!result?.success) {
        Log.warnAndToast(
          t(
            `general.RIDES_ACTIONS_WARNINGS.BULK_FIT_VEHICLE_AND_RECALCULATE_CANCELED`,
          ),
          selectedRideIds,
        );
      }
    }
  });

  const handleSendToSupplierClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.SEND_TO_SUPPLIER_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkSendToSupplier(selectedRideIds);
    }
  });

  const handleRebuildRouteClick = useEvent(async () => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.REBUILD_ROUTES_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      bulkRebuildRoutes(selectedRideIds);
    }
  });

  const actions: IBulkActionItem[] = [
    buildRideBulkAction({
      t,
      key: "sendToSupplier",
      onClick: handleSendToSupplierClick,
    }),
    buildRideBulkAction({
      t,
      key: "recalculatePrice",
      onClick: handleRecalculatePriceClick,
    }),
    buildRideBulkAction({
      t,
      key: "fitVehicleAndRecalculate",
      onClick: handleFitVehicleAndRecalculateClick,
    }),
    buildRideBulkAction({
      t,
      key: "rebuildRoutes",
      onClick: handleRebuildRouteClick,
    }),
    buildRideBulkAction({
      t,
      key: "assignJourney",
      onClick: handleJourneyAssignClick,
    }),
    buildRideBulkAction({ t, key: "updateRides", onClick: handleUpdateClick }),
    buildRideBulkAction({ t, key: "approve", onClick: noop }),
  ];

  return { actions, closeDrawer };
};
